// The painter's shared state for one board in one set: the seeded picks every client makes alike
// (docs/15 §3.2, docs/164 ET14) and the set's pieces by name.
import { cellHash, inPatch } from '../../tiling/hash';
import type { EncounterSet, Look, Piece } from '../types';
import { Canvas, type RGBA } from './canvas';
import { oklab } from './colour';
import type { Squares } from './board';

export const C = 24;

export class Ctx {
  /** The seed's salt: the kit and biome, never the season or the hour, so a board keeps its layout through the year and the night. */
  readonly salt: string;
  readonly look: Look;
  readonly built: boolean;
  private colours?: Array<{ c: RGBA; lab: [number, number, number] }>;
  /** A family drew the board's terraces and cliffs into the ground (docs/164 ET7): the renderer leaves its vector ledges off. */
  ledges = false;

  /** The pieces this set takes from its country's set (docs/186 EA14), by name. */
  readonly borrowed: ReadonlySet<string>;

  /** `country`: the set of the country an outdoor place stands in, whose water, rough and thicket a place without its own borrows (EA14). */
  constructor(readonly set: EncounterSet, readonly sq: Squares, seed: number, readonly country?: EncounterSet) {
    this.salt = `${set.kit}/${set.biome}#${seed >>> 0}`;
    this.look = set.look ?? {};
    this.borrowed = borrowedOf(set, country);
    // a built kit draws its walls from a top and a front (ET5); a country stands trees on its wall squares
    this.built = this.has('wall_top') && this.has('wall_face');
  }

  roll(purpose: string, x: number, y: number): number { return cellHash(this.salt, x, y, purpose); }
  patch(x: number, y: number, size: number, share: number, purpose: string): boolean { return inPatch(this.salt, x, y, size, share, purpose); }

  has(piece: string): boolean { return this.all(piece).length > 0; }
  all(piece: string): readonly Piece[] {
    const own = this.set.pieces.get(piece);
    if (own?.length) return own;
    return (this.borrowed.has(piece) && this.country?.pieces.get(piece)) || [];
  }
  /** Whether a file is the set's own (a borrowed one is not in its texture: what stands of it carries its pixels). */
  own(p: Piece): boolean { return !!this.set.pieces.get(p.piece)?.includes(p); }
  /** The drawing keys (`_shore`, `_shore_seasons`) of the set the board's water comes from. */
  get waterPrompts(): Record<string, unknown> { return this.borrowed.has('water') && this.country ? this.country.prompts : this.set.prompts; }
  /** The season of the set the board's water comes from. */
  get waterSeason(): string | undefined { return this.borrowed.has('water') && this.country ? this.country.season : this.set.season; }
  /** Every piece whose name starts with `prefix` (`decal_`, `grove_`), sorted by name. */
  named(prefix: string): string[] { return [...this.set.pieces.keys()].filter((p) => p.startsWith(prefix)).sort(); }

  /** One variant of `piece`, seeded by the square; `weights` (look.json `*_weights`) by variant order or file name. */
  pick(piece: string, purpose: string, x: number, y: number, weights?: Look['wall_face_weights']): Piece | undefined {
    const vs = this.all(piece);
    if (!vs.length) return undefined;
    const r = this.roll(purpose, x, y);
    if (weights) {
      const ws = vs.map((p, i) => (Array.isArray(weights) ? weights[i] ?? 1 : (weights as Record<string, number>)[p.frame] ?? (weights as Record<string, number>)[`${p.piece}_${String(p.v).padStart(2, '0')}`] ?? 1));
      let total = 0;
      for (const w of ws) total += w;
      if (total > 0) {
        let t = r % total;
        for (let i = 0; i < vs.length; i++) { t -= ws[i]!; if (t < 0) return vs[i]; }
      }
    }
    return vs[r % vs.length];
  }

  /** The nearest colour the set's own art uses (the tool snaps a computed colour to the kit's palette). */
  snap(c: RGBA): RGBA {
    if (!this.colours) {
      const seen = new Map<number, RGBA>();
      for (const vs of this.set.pieces.values()) for (const p of vs) {
        for (let i = 0; i < p.px.length; i += 4) {
          if (p.px[i + 3]! < 255) continue;
          const k = (p.px[i]! << 16) | (p.px[i + 1]! << 8) | p.px[i + 2]!;
          if (!seen.has(k)) seen.set(k, [p.px[i]!, p.px[i + 1]!, p.px[i + 2]!, 255]);
        }
      }
      this.colours = [...seen.values()].map((col) => ({ c: col, lab: oklab(col) }));
    }
    const [L, a, b] = oklab(c);
    let best = c, bd = Infinity;
    for (const { c: col, lab } of this.colours) {
      const d = (lab[0] - L) ** 2 + (lab[1] - a) ** 2 + (lab[2] - b) ** 2;
      if (d < bd) { bd = d; best = col; }
    }
    return [best[0], best[1], best[2], c[3]];
  }
}

/**
 * What a set borrows from its country (EA14), group by group, only when it has none of the group's own: the
 * liquids (a place with neither water nor deep), rough ground, and a thicket with what lies under it (a kit with a
 * named thicket, the courtyard's `thicket_rose`, draws its own). Sets with water keep it (the docks, the ashlands
 * courtyard's moat, the wetland ruins).
 */
const BORROW: ReadonlyArray<readonly string[]> = [['water', 'deep', 'ice'], ['rough'], ['thicket', 'thicket_under']];
const owns = (set: Pick<EncounterSet, 'pieces'>, p: string): boolean => !!set.pieces.get(p)?.length;
/** A set with neither `water` nor `deep` of its own. */
export function lacksWater(set: Pick<EncounterSet, 'pieces'>): boolean { return !owns(set, 'water') && !owns(set, 'deep'); }
/** Whether a set would take anything from its country: what decides whether the country's set is loaded at all. */
export function borrowsFromCountry(set: Pick<EncounterSet, 'pieces'>): boolean { return BORROW.some((g) => g.every((p) => !owns(set, p))); }
/** The set's own named thicket (`thicket_rose`), drawn for a thicket square when it has no plain `thicket`. */
export function namedThicket(set: Pick<EncounterSet, 'pieces'>): string | undefined {
  return owns(set, 'thicket') ? undefined : [...set.pieces.keys()].filter((p) => p.startsWith('thicket_') && p !== 'thicket_under' && owns(set, p)).sort()[0];
}
function borrowedOf(set: EncounterSet, country: EncounterSet | undefined): ReadonlySet<string> {
  const out = new Set<string>();
  if (!country || country === set) return out;
  for (const g of BORROW) {
    if (g.some((p) => owns(set, p)) || (g[0] === 'thicket' && namedThicket(set))) continue;
    for (const p of g) if (owns(country, p)) out.add(p);
  }
  return out;
}

/** A canvas the size of the board's ground. */
export const groundCanvas = (sq: Squares, fill?: RGBA): Canvas => new Canvas(sq.w * C, sq.h * C, fill);
